import { Fragment, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';

/**
 * Board in and out for a period: per board, the sheets at the start, what was
 * received (purchased), issued to jobs, extra used, returned and adjusted on a
 * stock count, and what was left at the end — and per job, where the sheets
 * went.  Paper Stock → Reports → Stock Movement.
 */

interface BoardRow {
  id: number; sku: string; board: string; brand: string; location: string; deleted: boolean;
  opening: number; received: number; issued: number; extra: number; returned: number; adjusted: number; used: number; closing: number;
  receivedKg: number | null; usedKg: number | null;
  openingValue: number; receivedValue: number; usedValue: number; closingValue: number;
}
type JobStatus = 'not_issued' | 'issued';
interface JobRun {
  run: number; label: string; reason: string; from: string | null;
  planned: number | null; used: number; status: JobStatus;
}
interface JobRow {
  jobId: string; client: string; product: string; boards: string[];
  /** In the period. */
  issued: number; extra: number; returned: number; used: number; value: number; lastOn: string | null;
  /** Against the job card, all to date. */
  planned: number | null; usedToDate: number; status: JobStatus;
  jobStatus: string; createdOn: string | null; reissued: number; runs: JobRun[];
  /** Closed (by Super Admin) or cancelled - listed only for the board it took. */
  closed: boolean;
}

const STATUS: Record<JobStatus, { label: string; cls: string }> = {
  not_issued: { label: 'Not issued', cls: 'bg-slate-200 text-slate-700' },
  issued: { label: 'Issued', cls: 'bg-emerald-100 text-emerald-800' },
};
const Badge = ({ s }: { s: JobStatus }) => (
  <span className={`whitespace-nowrap rounded px-1.5 py-0.5 text-[11px] font-semibold ${STATUS[s].cls}`}>{STATUS[s].label}</span>
);
const day = (d: string | null) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '');

const pad = (x: number) => String(x).padStart(2, '0');
const iso = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const n = (v: number) => v.toLocaleString('en-IN');
const rs = (v: number) => `₹${v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const kgT = (v: number | null) => (v == null ? '—' : `${v.toLocaleString('en-IN', { maximumFractionDigits: 2 })} kg`);
const dash = (v: number, cls = '') => (v ? <span className={cls}>{n(v)}</span> : <span className="text-slate-300">—</span>);

function thisMonth() { const d = new Date(); return { from: iso(new Date(d.getFullYear(), d.getMonth(), 1)), to: iso(d) }; }
function lastMonth() { const d = new Date(); return { from: iso(new Date(d.getFullYear(), d.getMonth() - 1, 1)), to: iso(new Date(d.getFullYear(), d.getMonth(), 0)) }; }

function csv(name: string, rows: (string | number)[][]) {
  const text = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\r\n');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob(['﻿' + text], { type: 'text/csv' }));
  a.download = name;
  a.click();
}

export function BoardMovementReport() {
  const [range, setRange] = useState(thisMonth);
  const [view, setView] = useState<'boards' | 'jobs'>('boards');
  const [q, setQ] = useState('');
  const [onlyMoved, setOnlyMoved] = useState(true);
  const [jobFilter, setJobFilter] = useState<JobStatus | 'all' | 'reissued'>('all');
  const [openJob, setOpenJob] = useState<string | null>(null);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['board-movement', range.from, range.to],
    queryFn: () => api<{ boards: BoardRow[]; jobs: JobRow[] }>(`/api/paper-stock/reports/movement?from=${range.from}&to=${range.to}`),
    enabled: !!range.from && !!range.to && range.from <= range.to,
  });

  const needle = q.trim().toLowerCase();
  const boards = useMemo(() => (data?.boards ?? []).filter((b) =>
    (!onlyMoved || b.received || b.used || b.adjusted)
    && (!needle || `${b.sku} ${b.board} ${b.brand} ${b.location}`.toLowerCase().includes(needle))), [data, onlyMoved, needle]);
  const searchedJobs = useMemo(() => (data?.jobs ?? []).filter((j) =>
    !needle || `${j.jobId} ${j.client} ${j.product} ${j.boards.join(' ')}`.toLowerCase().includes(needle)), [data, needle]);
  const jobs = useMemo(() => searchedJobs.filter((j) =>
    jobFilter === 'all' || (jobFilter === 'reissued' ? j.reissued > 0 : j.status === jobFilter)), [searchedJobs, jobFilter]);
  const jobCount = (f: JobStatus | 'reissued') => searchedJobs.filter((j) => (f === 'reissued' ? j.reissued > 0 : j.status === f)).length;

  const t = useMemo(() => boards.reduce((s, b) => ({
    opening: s.opening + b.opening, received: s.received + b.received, used: s.used + b.used, closing: s.closing + b.closing,
    issued: s.issued + b.issued, extra: s.extra + b.extra, returned: s.returned + b.returned, adjusted: s.adjusted + b.adjusted,
    receivedKg: s.receivedKg + (b.receivedKg ?? 0), usedKg: s.usedKg + (b.usedKg ?? 0),
    receivedValue: s.receivedValue + b.receivedValue, usedValue: s.usedValue + b.usedValue,
    openingValue: s.openingValue + b.openingValue, closingValue: s.closingValue + b.closingValue,
  }), { opening: 0, received: 0, used: 0, closing: 0, issued: 0, extra: 0, returned: 0, adjusted: 0, receivedKg: 0, usedKg: 0, receivedValue: 0, usedValue: 0, openingValue: 0, closingValue: 0 }), [boards]);

  const card = (label: string, sheets: number, sub: string, cls: string) => (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`text-xl font-bold tabular-nums ${cls}`}>{n(sheets)} <span className="text-xs font-medium text-slate-400">sheets</span></div>
      <div className="text-xs text-slate-500">{sub}</div>
    </div>
  );

  const download = () => {
    const name = `board-${view === 'boards' ? 'movement' : 'by-job'}-${range.from}-to-${range.to}.csv`;
    if (view === 'boards') {
      csv(name, [['SKU', 'Board', 'Brand', 'Location', 'Opening', 'Received', 'Received kg', 'Received value', 'Issued', 'Extra used', 'Returned', 'Net used', 'Used kg', 'Used value', 'Stock count +/-', 'Closing', 'Closing value'],
        ...boards.map((b) => [b.sku, b.board, b.brand, b.location, b.opening, b.received, b.receivedKg ?? '', b.receivedValue, b.issued, b.extra, b.returned, b.used, b.usedKg ?? '', b.usedValue, b.adjusted, b.closing, b.closingValue])]);
    } else {
      csv(name, [['Job', 'Client', 'Product', 'Run', 'Reason', 'Run from', 'Planned sheets', 'Used to date', 'Status', 'Boards', 'Issued (period)', 'Extra used (period)', 'Returned (period)', 'Net used (period)', 'Value (period)', 'Last movement'],
        ...jobs.flatMap((j) => [
          [j.jobId, j.client, j.product, j.reissued ? 'All runs' : '', '', '', j.planned ?? '', j.usedToDate, STATUS[j.status].label, j.boards.join(' '),
            j.issued, j.extra, j.returned, j.used, j.value, j.lastOn ? new Date(j.lastOn).toLocaleString('en-IN') : ''],
          ...(j.reissued ? j.runs.map((r) => [j.jobId, '', '', r.label, r.reason, day(r.from), r.planned ?? '', r.used, STATUS[r.status].label, '', '', '', '', '', '', '']) : []),
        ])]);
    }
  };

  const pill = (on: boolean) => `px-3 py-1 rounded-md text-sm font-medium ${on ? 'bg-white text-brand-700 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-2">
        <div>
          <label className="label">From</label>
          <input type="date" className="input !w-auto" value={range.from} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} />
        </div>
        <div>
          <label className="label">To</label>
          <input type="date" className="input !w-auto" value={range.to} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} />
        </div>
        <button className="btn-ghost text-sm" onClick={() => setRange(thisMonth())}>This month</button>
        <button className="btn-ghost text-sm" onClick={() => setRange(lastMonth())}>Last month</button>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <input className="input !w-56" placeholder="Search SKU, board, job, client…" value={q} onChange={(e) => setQ(e.target.value)} />
          <button className="btn-ghost text-sm" onClick={download} disabled={!data}>Download CSV</button>
        </div>
      </div>

      {isLoading && <div className="text-sm text-slate-500">Loading…</div>}
      {isError && <div className="text-sm text-red-600">Could not load: {(error as Error).message}</div>}
      {data && (
        <>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            {card('At the start', t.opening, rs(t.openingValue), 'text-slate-800')}
            {card('Received (purchased)', t.received, `${kgT(t.receivedKg)} · ${rs(t.receivedValue)}`, 'text-emerald-700')}
            {card('Used by jobs', t.used, `${kgT(t.usedKg)} · ${rs(t.usedValue)} · issued ${n(t.issued)}, extra ${n(t.extra)}, returned ${n(t.returned)}`, 'text-red-700')}
            {card('At the end', t.closing, `${rs(t.closingValue)}${t.adjusted ? ` · stock count ${t.adjusted > 0 ? '+' : ''}${n(t.adjusted)}` : ''}`, 'text-brand-700')}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="inline-flex rounded-lg bg-slate-100 p-1">
              <button className={pill(view === 'boards')} onClick={() => setView('boards')}>By board ({boards.length})</button>
              <button className={pill(view === 'jobs')} onClick={() => setView('jobs')}>By job — where it went ({jobs.length})</button>
            </div>
            {view === 'boards' && (
              <label className="flex items-center gap-1.5 text-sm text-slate-600">
                <input type="checkbox" checked={onlyMoved} onChange={(e) => setOnlyMoved(e.target.checked)} />
                Only boards that moved
              </label>
            )}
          </div>

          <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
            {view === 'boards' ? (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs text-slate-600">
                  <tr>
                    <th className="px-3 py-2 text-left">Board</th>
                    <th className="px-3 py-2 text-right">At start</th>
                    <th className="px-3 py-2 text-right">Received</th>
                    <th className="px-3 py-2 text-right">Issued</th>
                    <th className="px-3 py-2 text-right">Extra used</th>
                    <th className="px-3 py-2 text-right">Returned</th>
                    <th className="px-3 py-2 text-right">Net used</th>
                    <th className="px-3 py-2 text-right">Stock count</th>
                    <th className="px-3 py-2 text-right">At end</th>
                    <th className="px-3 py-2 text-right">Used value</th>
                  </tr>
                </thead>
                <tbody>
                  {boards.length === 0 && <tr><td colSpan={10} className="p-6 text-center text-slate-400">Nothing moved in this period.</td></tr>}
                  {boards.map((b) => (
                    <tr key={b.id} className="border-t border-slate-100">
                      <td className="px-3 py-1.5">
                        <span className="font-medium">{b.sku}</span>{b.deleted && <span className="ml-1 text-[10px] text-slate-400">(removed)</span>}
                        <div className="text-xs text-slate-500">{[b.board, b.brand, b.location].filter(Boolean).join(' · ')}</div>
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{n(b.opening)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{dash(b.received, 'text-emerald-700 font-medium')}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{dash(b.issued)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{dash(b.extra, 'text-red-700')}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{dash(b.returned, 'text-emerald-700')}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums font-semibold">{dash(b.used)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{b.adjusted ? `${b.adjusted > 0 ? '+' : ''}${n(b.adjusted)}` : <span className="text-slate-300">—</span>}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums font-semibold">{n(b.closing)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{b.usedValue ? rs(b.usedValue) : <span className="text-slate-300">—</span>}</td>
                    </tr>
                  ))}
                </tbody>
                {boards.length > 0 && (
                  <tfoot className="border-t-2 border-slate-200 bg-slate-50 font-semibold">
                    <tr>
                      <td className="px-3 py-2">Total</td>
                      <td className="px-3 py-2 text-right tabular-nums">{n(t.opening)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{n(t.received)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{n(t.issued)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{n(t.extra)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{n(t.returned)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{n(t.used)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{t.adjusted ? n(t.adjusted) : '—'}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{n(t.closing)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{rs(t.usedValue)}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            ) : (
              <>
              <div className="flex flex-wrap gap-1.5 border-b border-slate-100 p-2 text-xs">
                {([['all', `All (${searchedJobs.length})`], ['not_issued', `Not issued (${jobCount('not_issued')})`],
                   ['issued', `Issued (${jobCount('issued')})`], ['reissued', `Reissued (${jobCount('reissued')})`]] as const).map(([k, label]) => (
                  <button key={k} onClick={() => setJobFilter(k)}
                          className={`rounded-full border px-2.5 py-0.5 ${jobFilter === k ? 'border-brand bg-brand text-white' : 'border-slate-300 text-slate-600 hover:border-brand'}`}>
                    {label}
                  </button>
                ))}
              </div>
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs text-slate-600">
                  <tr>
                    <th className="px-3 py-2 text-left">Job</th>
                    <th className="px-3 py-2 text-left">Client · Product</th>
                    <th className="px-3 py-2 text-right" title="Sheets on the job card, incl. wastage - every run added up when it was reissued">Planned</th>
                    <th className="px-3 py-2 text-right" title="Net sheets taken, all to date">Used to date</th>
                    <th className="px-3 py-2 text-left">Status</th>
                    <th className="px-3 py-2 text-right" title="Issued + extra − returned, in this period">This period</th>
                    <th className="px-3 py-2 text-left">Board</th>
                    <th className="px-3 py-2 text-right">Value</th>
                    <th className="px-3 py-2 text-left">Last movement</th>
                  </tr>
                </thead>
                <tbody>
                  {jobs.length === 0 && <tr><td colSpan={9} className="p-6 text-center text-slate-400">No jobs here for this period.</td></tr>}
                  {jobs.map((j) => (
                    <Fragment key={j.jobId}>
                      <tr className="border-t border-slate-100 align-top">
                        <td className="px-3 py-1.5 whitespace-nowrap">
                          <span className="font-mono text-xs font-semibold text-brand-700">{j.jobId}</span>
                          {j.reissued > 0 && (
                            <button className="ml-1.5 rounded bg-violet-100 px-1.5 py-0.5 text-[10px] font-semibold text-violet-800 hover:bg-violet-200"
                                    onClick={() => setOpenJob(openJob === j.jobId ? null : j.jobId)} title="See each run">
                              Reissued{j.reissued > 1 ? ` ×${j.reissued}` : ''} {openJob === j.jobId ? '▴' : '▾'}
                            </button>
                          )}
                        </td>
                        <td className="px-3 py-1.5">{j.client}<div className="text-xs text-slate-500">{j.product}</div></td>
                        <td className="px-3 py-1.5 text-right tabular-nums">{j.planned != null ? n(j.planned) : <span className="text-slate-300">—</span>}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums font-semibold">{dash(j.usedToDate)}</td>
                        <td className="px-3 py-1.5">
                          <Badge s={j.status} />
                          {j.closed && (
                            <span className="ml-1 whitespace-nowrap rounded bg-slate-700 px-1.5 py-0.5 text-[11px] font-semibold text-white" title="Job card closed or cancelled">
                              {/^cancel/i.test(j.jobStatus) ? 'Cancelled' : 'Closed'}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-1.5 text-right tabular-nums">
                          {dash(j.used)}
                          {(j.extra > 0 || j.returned > 0) && (
                            <div className="text-[11px] text-slate-500">{j.extra > 0 && <span className="text-red-700">+{n(j.extra)} extra </span>}{j.returned > 0 && <span className="text-emerald-700">−{n(j.returned)} back</span>}</div>
                          )}
                        </td>
                        <td className="px-3 py-1.5 text-xs text-slate-600">{j.boards.join(', ') || <span className="text-slate-300">—</span>}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums">{j.value ? rs(j.value) : <span className="text-slate-300">—</span>}</td>
                        <td className="px-3 py-1.5 text-xs text-slate-500 whitespace-nowrap">
                          {j.lastOn ? new Date(j.lastOn).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
                            : <span title="Job card raised in the period">card {day(j.createdOn)}</span>}
                        </td>
                      </tr>
                      {openJob === j.jobId && j.runs.map((r) => (
                        <tr key={`${j.jobId}-${r.run}`} className="bg-violet-50/50 text-xs">
                          <td className="py-1.5 pl-8 pr-3 font-medium text-violet-900" colSpan={2}>
                            {r.label}{r.from && <> · {day(r.from)}</>}
                            {r.reason && <div className="font-normal text-violet-800">{r.reason}</div>}
                          </td>
                          <td className="px-3 py-1.5 text-right tabular-nums">{r.planned != null ? n(r.planned) : '—'}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums font-semibold">{dash(r.used)}</td>
                          <td className="px-3 py-1.5"><Badge s={r.status} /></td>
                          <td colSpan={4} />
                        </tr>
                      ))}
                    </Fragment>
                  ))}
                </tbody>
                {jobs.length > 0 && (
                  <tfoot className="border-t-2 border-slate-200 bg-slate-50 font-semibold">
                    <tr>
                      <td className="px-3 py-2" colSpan={2}>Total ({jobs.length} jobs)</td>
                      <td className="px-3 py-2 text-right tabular-nums">{n(jobs.reduce((s, j) => s + (j.planned ?? 0), 0))}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{n(jobs.reduce((s, j) => s + j.usedToDate, 0))}</td>
                      <td />
                      <td className="px-3 py-2 text-right tabular-nums">{n(jobs.reduce((s, j) => s + j.used, 0))}</td>
                      <td />
                      <td className="px-3 py-2 text-right tabular-nums">{rs(jobs.reduce((s, j) => s + j.value, 0))}</td>
                      <td />
                    </tr>
                  </tfoot>
                )}
              </table>
              </>
            )}
          </div>
          <p className="text-[11px] text-slate-400">
            Days are Indian days, both dates included. A reversed movement counts against what it undid. Net used = issued + extra used − returned.
            By job: Planned is the job card's sheets incl. wastage; Used to date is everything it has taken, all dates. A reissued card (e.g. against
            rejection) keeps its number - open "Reissued" to see each run's planned sheets and what it took. Issued means it has taken board.
            Weight from each board's size and GSM; value at its current rate per kg, the same way the stock is valued.
          </p>
        </>
      )}
    </div>
  );
}
